import FormHelperText from '@mui/material/FormHelperText';
import { BaseCheckbox, BaseTextField, ModalWrapper, SectionTitle } from 'modules/shared/components';
import {
  INPUT_MODES,
  INPUT_TYPES,
} from 'modules/shared/components/form/TextField/textField.constants';
import { combineToMinutes, splitMinutes } from 'modules/shared/utils';
import { TASK_STATUS } from 'modules/work-implementation/constants';
import { useEffect, useMemo, useState } from 'react';
import { groupTasksByGroupKey } from '../utils';

export default function TaskAssignmentModal({
  open,
  onClose,
  onConfirm,
  tasks,
  selectedTasks,
  taskType,
}) {
  const [selectedMap, setSelectedMap] = useState({});
  const [errorMap, setErrorMap] = useState({});

  const groupedTasks = useMemo(() => groupTasksByGroupKey(tasks), [tasks]);
  const selectableTasks = useMemo(
    () => tasks.filter((t) => t.status === TASK_STATUS.UNASSIGNED),
    [tasks],
  );

  const allSelected =
    selectableTasks.length > 0 &&
    selectableTasks.every((task) => selectedMap[task.taskKey]?.checked);

  const isSubmitDisabled =
    selectableTasks.length === 0 ||
    selectableTasks.every((task) => !selectedMap[task.taskKey]?.checked);

  // Preserve tasks with entered time when deselecting all
  const handleSelectAllChange = (checked) => {
    setSelectedMap((prev) => {
      const next = { ...prev };

      selectableTasks.forEach((task) => {
        const existing = prev[task.taskKey];
        const hasTime = (existing?.minutes ?? 0) > 0;

        if (checked) {
          next[task.taskKey] = {
            checked: true,
            minutes: existing?.minutes ?? 0,
          };
        } else {
          if (hasTime) {
            next[task.taskKey] = {
              checked: true,
              minutes: existing.minutes,
            };
          } else {
            next[task.taskKey] = {
              checked: false,
              minutes: 0,
            };
          }
        }
      });

      return next;
    });

    setErrorMap({});
  };

  useEffect(() => {
    if (!open || Object.keys(selectedMap).length > 0) return;

    const initial = {};
    tasks.forEach((task) => {
      if (task.status !== TASK_STATUS.UNASSIGNED) {
        initial[task.taskKey] = {
          checked: true,
          minutes: 0,
        };
      }
    });

    (selectedTasks || []).forEach((task) => {
      initial[task.taskKey] = {
        checked: true,
        minutes: task.minutes,
      };
    });

    setSelectedMap(initial);
  }, [open, tasks, selectedTasks, selectedMap]);

  const handleConfirm = () => {
    const errors = {};
    const selected = [];

    tasks.forEach((task) => {
      const entry = selectedMap[task.taskKey];

      if (!entry?.checked) return;
      if (task.status !== TASK_STATUS.UNASSIGNED) return;

      const minutes = entry.minutes ?? 0;

      if (minutes <= 0) {
        errors[task.taskKey] = 'Time is required';
        return;
      }

      selected.push({
        taskKey: task.taskKey,
        taskName: task.name,
        minutes,
      });
    });

    if (Object.keys(errors).length > 0) {
      setErrorMap(errors);
      return;
    }

    setErrorMap({});
    onConfirm(selected);
  };

  const clearError = (taskKey) => {
    setErrorMap((prev) => {
      if (!prev[taskKey]) return prev;
      const next = { ...prev };
      delete next[taskKey];
      return next;
    });
  };

  return (
    <ModalWrapper open={open} onClose={onClose} maxWidth='fit-content'>
      <div className='column gap-5'>
        <SectionTitle title='Select Tasks' />

        <div className='column gap-1'>
          <BaseCheckbox
            id='select-all-tasks'
            label='Select all'
            checked={allSelected}
            disabled={selectableTasks.length === 0}
            onChange={handleSelectAllChange}
            wrapperSx={{ mb: 4 }}
          />

          {Object.values(groupedTasks).map(({ label, tasks }) => (
            <div key={label} className='column gap-1'>
              <span className='font-medium label'>{label}</span>

              <div className='grid grid--2-cols gap-8'>
                {tasks.map((task) => {
                  const disabled = task.status !== TASK_STATUS.UNASSIGNED;
                  const entry = selectedMap[task.taskKey];
                  const checked = Boolean(entry?.checked);
                  const totalMinutes = entry?.minutes ?? 0;
                  const { hours, minutes } = splitMinutes(totalMinutes);

                  return (
                    <div key={task.taskKey} className='column gap-0'>
                      <div className='row gap-5 row--between'>
                        <BaseCheckbox
                          id={task.taskKey}
                          label={task.name}
                          checked={checked}
                          disabled={disabled}
                          onChange={(checked) => {
                            setSelectedMap((prev) => ({
                              ...prev,
                              [task.taskKey]: {
                                checked,
                                minutes: checked ? (prev[task.taskKey]?.minutes ?? 0) : 0,
                              },
                            }));

                            clearError(task.taskKey);
                          }}
                        />

                        {!disabled && (
                          <div className='row gap-1'>
                            <BaseTextField
                              id={`${task.taskKey}-hours`}
                              value={hours}
                              onChange={(value) => {
                                const nextHours = Number(value) || 0;
                                const total = combineToMinutes(nextHours, minutes);

                                setSelectedMap((prev) => ({
                                  ...prev,
                                  [task.taskKey]: {
                                    ...prev[task.taskKey],
                                    minutes: total,
                                  },
                                }));

                                clearError(task.taskKey);
                              }}
                              type={INPUT_TYPES.NUMBER}
                              inputMode={INPUT_MODES.NUMERIC}
                              placeholder='hrs'
                              hideArrow
                              sx={{ width: 80 }}
                              disabled={!checked || disabled}
                              inputProps={{ min: 0 }}
                              suffix='hrs'
                            />
                            <span>:</span>
                            <BaseTextField
                              id={`${task.taskKey}-minutes`}
                              value={minutes}
                              onChange={(value) => {
                                const nextMinutes = Number(value) || 0;
                                const clamped = Math.min(59, Math.max(0, nextMinutes));
                                const total = combineToMinutes(hours, clamped);

                                setSelectedMap((prev) => ({
                                  ...prev,
                                  [task.taskKey]: {
                                    ...prev[task.taskKey],
                                    minutes: total,
                                  },
                                }));

                                clearError(task.taskKey);
                              }}
                              type={INPUT_TYPES.NUMBER}
                              inputMode={INPUT_MODES.NUMERIC}
                              placeholder='min'
                              hideArrow
                              sx={{ width: 80 }}
                              disabled={!checked || disabled}
                              inputProps={{ min: 0, max: 59 }}
                              suffix='mins'
                            />
                          </div>
                        )}
                      </div>
                      <FormHelperText
                        error
                        sx={{
                          mt: 0,
                          minHeight: 20,
                          fontSize: '0.75rem',
                          alignSelf: 'flex-end',
                        }}
                      >
                        {errorMap[task.taskKey] ?? ''}
                      </FormHelperText>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className='row gap-3 row--center'>
          <button type='button' className='btn outlined-btn' onClick={onClose}>
            Cancel
          </button>
          <button
            type='button'
            className='btn cta-btn'
            onClick={handleConfirm}
            disabled={isSubmitDisabled}
          >
            Submit
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}
